import { useMemo, useRef, type KeyboardEvent } from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EventView, RegisterRow } from '@/api/projections';
import { statusLabel } from '@/components/statusLabel';
import { occurrenceLabelValues } from '@/features/planned/paycheckParts';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { categoricalFor } from '@/theme/categorical';
import { toneFor, toneMarker } from '@/theme/statusTones';
import { resolveTokens } from '@/theme/theme';
import type { AppTokens } from '@/theme/tokens';
import { monthBounds } from './horizon';
import type { ChartSeries, DrillDown } from './types';
import { useContainerWidth } from './useContainerWidth';

const FALLBACK_WIDTH = 640;
const PAD = { top: 16, right: 12, bottom: 24, left: 12 };

const RESOLVED = new Set(['MATCHED', 'POSTED', 'MISSED', 'SUPERSEDED']);

interface ProjectionChartProps {
  from: string;
  to: string;
  series: ChartSeries[];
  events: EventView[];
  register: RegisterRow[];
  currency: string | null;
  minimum?: { date: string; balanceMinor: number } | null;
  hiddenIds?: ReadonlySet<string>;
  onToggleSeries?: (id: string) => void;
  /** Omit in a read-only surface (session looking-ahead). */
  onDrill?: (drill: DrillDown) => void;
}

function civilDays(civil: string): number {
  return (
    Date.UTC(Number(civil.slice(0, 4)), Number(civil.slice(5, 7)) - 1, Number(civil.slice(8, 10))) /
    86400000
  );
}

function niceStep(span: number, count: number): number {
  const raw = span / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(Math.abs(raw) || 1));
  const norm = raw / mag;
  const nice = norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1;
  return nice * mag;
}

function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) {
    const pad = Math.abs(min) || 100;
    min -= pad;
    max += pad;
  }
  const step = niceStep(max - min, count - 1);
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(v);
  return ticks;
}

function monthStarts(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const endY = Number(to.slice(0, 4));
  const endM = Number(to.slice(5, 7));
  while (y < endY || (y === endY && m <= endM)) {
    out.push(`${y}-${String(m).padStart(2, '0')}-01`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** A series' stroke: the brand, `text2`, or its categorical data colour's `line` (3:1 on surface). */
function seriesColor(tokens: AppTokens, s: ChartSeries): string {
  if (s.color === 'brand') return tokens.brandBoundary;
  if (s.color === 'text2') return tokens.text2;
  return categoricalFor(s.categoricalId ?? 0, tokens).line;
}

function stateClass(event: EventView, differs: boolean): string {
  const parts = [`event-${event.state ?? 'PLANNED'}`];
  if (event.pending === true) parts.push('event-pending');
  if (event.state !== null && RESOLVED.has(event.state)) parts.push('event-resolved');
  if (differs) parts.push('event-differs');
  return parts.join(' ');
}

export function ProjectionChart({
  from,
  to,
  series,
  events,
  register,
  currency,
  minimum = null,
  hiddenIds,
  onToggleSeries,
  onDrill,
}: ProjectionChartProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  // An SVG axis label: a string, as the chart's other labels (TMMAPP-0113 4d).
  const axisLabel = (minor: number) => formatMoneyMinor(minor, currency, locale);
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const xs = !md;
  // 230 at md and up (the prototype's account forecast, P:1202), 200 below.
  const height = md ? 230 : 200;
  const frameRef = useRef<HTMLDivElement>(null);
  const measured = useContainerWidth(frameRef);
  const width = measured > 0 ? measured : FALLBACK_WIDTH;
  const today = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  })();

  const labelInk = tokens.textLabel('surface');
  const visible = series.filter((s) => hiddenIds === undefined || !hiddenIds.has(s.id));

  const geometry = useMemo(() => {
    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const x0 = civilDays(from);
    const x1 = Math.max(x0 + 1, civilDays(to));
    const values = visible.flatMap((s) =>
      s.points.flatMap((p) => (p.balanceMinor === null ? [] : [p.balanceMinor])),
    );
    if (minimum !== null) values.push(minimum.balanceMinor);
    const dataMin = values.length > 0 ? Math.min(...values, 0) : 0;
    const dataMax = values.length > 0 ? Math.max(...values, 0) : 1;
    const ticks = niceTicks(dataMin, dataMax);
    const yMin = ticks[0] ?? dataMin;
    const yMax = ticks[ticks.length - 1] ?? dataMax;
    const ySpan = yMax - yMin || 1;
    const x = (civil: string) => PAD.left + ((civilDays(civil) - x0) / (x1 - x0)) * plotW;
    const y = (value: number) => PAD.top + ((yMax - value) / ySpan) * plotH;
    const months = monthStarts(from, to);
    const zeroY = yMin <= 0 && yMax >= 0 ? y(0) : null;
    const paths = visible.map((s) => {
      const pts = s.points.filter((p) => p.date >= from && p.date <= to);
      const d = pts
        .map((p, i) =>
          p.balanceMinor === null
            ? ''
            : `${i === 0 || pts[i - 1]?.balanceMinor === null ? 'M' : 'L'}${x(p.date).toFixed(1)},${y(p.balanceMinor).toFixed(1)}`,
        )
        .join(' ');
      return { series: s, d, color: seriesColor(tokens, s) };
    });
    const primary = visible[0];
    const hi = values.length > 0 ? Math.max(...values) : null;
    const lo = values.length > 0 ? Math.min(...values) : null;
    return { plotW, plotH, x, y, months, zeroY, yMin, paths, primary, hi, lo };
  }, [from, to, visible, height, tokens, minimum, width]);

  const differsBy = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const row of register) {
      if (row.differsFromSeries === null) continue;
      const key = `${row.seriesId ?? ''}|${row.occursOn ?? row.date}`;
      const d = row.differsFromSeries;
      map.set(key, d.date || d.amount || d.slices);
    }
    return map;
  }, [register]);

  function handleKey(event: KeyboardEvent, action: () => void) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }

  return (
    <Box ref={frameRef} sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <svg
        // A group, not an img: the month regions inside are focusable buttons (axe nested-interactive).
        role="group"
        aria-label={t('cashflow.chartLabel', {
          from: formatCivilDate(from, locale),
          to: formatCivilDate(to, locale),
        })}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        style={{ display: 'block' }}
      >
        {geometry.zeroY !== null && geometry.yMin < 0 && (
          <rect
            className="negative-wash"
            x={PAD.left}
            y={geometry.zeroY}
            width={geometry.plotW}
            height={height - PAD.bottom - geometry.zeroY}
            fill={tokens.moneyNegativeWash}
          />
        )}
        {geometry.months.map((start, index) => {
          const bounds = monthBounds(start);
          const x0 = geometry.x(bounds.from < from ? from : bounds.from);
          const x1 = geometry.x(bounds.to > to ? to : bounds.to);
          const drillMonth = () =>
            onDrill?.({
              from: bounds.from < from ? from : bounds.from,
              to: bounds.to > to ? to : bounds.to,
            });
          const label = formatCivilDate(start, locale, { month: 'short' });
          const showLabel = !xs || index % 2 === 0;
          return (
            <g key={start}>
              <rect
                {...(onDrill
                  ? {
                      role: 'button' as const,
                      // Named only as a control: a read-only month region is decorative.
                      'aria-label': t('cashflow.monthRegion', { month: label }),
                      tabIndex: 0,
                      onClick: drillMonth,
                      onKeyDown: (event: KeyboardEvent<SVGRectElement>) =>
                        handleKey(event, drillMonth),
                      style: { cursor: 'pointer' },
                    }
                  : {})}
                x={x0}
                y={PAD.top}
                width={Math.max(1, x1 - x0)}
                height={geometry.plotH}
                fill="transparent"
              />
              {showLabel && (
                <text
                  x={(x0 + x1) / 2}
                  y={height - 8}
                  textAnchor="middle"
                  fill={labelInk}
                  fontSize={10}
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {geometry.zeroY !== null && (
          <line
            x1={PAD.left}
            x2={width - PAD.right}
            y1={geometry.zeroY}
            y2={geometry.zeroY}
            stroke={tokens.line}
            strokeWidth={1}
          >
            <title>{t('cashflow.zero')}</title>
          </line>
        )}
        {today >= from && today <= to && (
          <g>
            <line
              x1={geometry.x(today)}
              x2={geometry.x(today)}
              y1={PAD.top}
              y2={height - PAD.bottom}
              stroke={tokens.text}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <text x={geometry.x(today) + 4} y={PAD.top + 12} fill={tokens.text} fontSize={11}>
              {t('cashflow.today')}
            </text>
          </g>
        )}
        {geometry.paths.map((path) =>
          path.d.length > 0 ? (
            <path
              key={path.series.id}
              d={path.d}
              fill="none"
              stroke={path.color}
              strokeWidth={path.series.faint ? 1 : 2}
              strokeDasharray={path.series.dashed ? '6 4' : undefined}
            />
          ) : null,
        )}
        {minimum !== null && minimum.date >= from && minimum.date <= to && (
          <g>
            <circle
              cx={geometry.x(minimum.date)}
              cy={geometry.y(minimum.balanceMinor)}
              r={5}
              fill={minimum.balanceMinor < 0 ? tokens.moneyNegative : tokens.brandBoundary}
            />
            <text
              x={geometry.x(minimum.date) + 8}
              y={geometry.y(minimum.balanceMinor) - 8}
              fill={tokens.text}
              fontSize={11}
            >
              {t('cashflow.minimumLabel')}
            </text>
          </g>
        )}
        {geometry.primary &&
          geometry.primary.points.length > 0 &&
          (() => {
            const last = geometry.primary.points[geometry.primary.points.length - 1];
            if (!last || last.balanceMinor === null || last.date < from || last.date > to)
              return null;
            return (
              <circle
                cx={geometry.x(last.date)}
                cy={geometry.y(last.balanceMinor)}
                r={3.5}
                fill={tokens.brandBoundary}
              >
                <title>{t('cashflow.end')}</title>
              </circle>
            );
          })()}
        {geometry.hi !== null && geometry.lo !== null && (
          <g data-hi-lo="">
            <text x={PAD.left} y={PAD.top + 8} fill={labelInk} fontSize={11}>
              {axisLabel(geometry.hi)}
            </text>
            <text x={PAD.left} y={height - PAD.bottom - 4} fill={labelInk} fontSize={11}>
              {axisLabel(geometry.lo)}
            </text>
          </g>
        )}
        {events.map((event, index) => {
          const yVal = event.runningMinor;
          if (event.occursOn < from || event.occursOn > to) return null;
          const differs = differsBy.get(`${event.seriesId ?? ''}|${event.occursOn}`) === true;
          const cx = geometry.x(event.occursOn);
          const cy = geometry.y(yVal);
          // The marker is the state's tone (the registry's, as in the register), painted `<tone>Fg`.
          const color = toneMarker(tokens, toneFor('occurrence', event.state ?? 'PLANNED'));
          const drillEvent = () =>
            onDrill?.({
              from: event.occursOn,
              to: event.occursOn,
              highlight: {
                date: event.occursOn,
                ...(event.seriesId !== null ? { seriesId: event.seriesId } : {}),
              },
            });
          // The state is in the name too, so the marker's colour is never the only cue.
          const label = t('cashflow.eventLabel', {
            date: formatCivilDate(event.occursOn, locale),
            payee: event.payee ?? '—',
            amount: formatMoneyMinor(event.amountMinor, currency, locale),
            state: statusLabel(
              t,
              'occurrence',
              event.state ?? 'PLANNED',
              occurrenceLabelValues(event.amountMinor),
            ),
          });
          return (
            <g
              key={`${event.occursOn}-${event.seriesId ?? index}`}
              {...(onDrill
                ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    onClick: drillEvent,
                    onKeyDown: (ev: KeyboardEvent<SVGGElement>) => handleKey(ev, drillEvent),
                    style: { cursor: 'pointer' },
                  }
                : {})}
              className={stateClass(event, differs)}
              aria-label={label}
            >
              {event.pending === true ? (
                <circle cx={cx} cy={cy} r={5} fill="none" stroke={color} strokeWidth={2} />
              ) : (
                <circle cx={cx} cy={cy} r={3.5} fill={color} />
              )}
              {event.state !== null && RESOLVED.has(event.state) && (
                <text x={cx} y={cy + 3} textAnchor="middle" fill={tokens.text} fontSize={10}>
                  ×
                </text>
              )}
              {differs && (
                <text x={cx + 7} y={cy - 6} fill={tokens.warningFg} fontSize={10}>
                  !
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Stack
        direction="row"
        sx={{ gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}
        component="ul"
      >
        {series.map((s) => {
          const color = seriesColor(tokens, s);
          const label = s.nameKey === 'named' ? s.name : t(`cashflow.${s.nameKey}`);
          const hidden = hiddenIds?.has(s.id) === true;
          return (
            <Box
              key={s.id}
              component="li"
              sx={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => onToggleSeries?.(s.id)}
                aria-pressed={!hidden}
                aria-label={label}
                sx={{
                  border: 0,
                  background: 'none',
                  p: 0,
                  cursor: onToggleSeries ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                {hidden ? (
                  // Toggled off: disabled-class text3 and a hollow lineStrong swatch, not a fade.
                  <Box
                    sx={{
                      width: 16,
                      height: 10,
                      boxSizing: 'border-box',
                      border: `2px solid ${tokens.lineStrong}`,
                      bgcolor: 'transparent',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 16,
                      height: 0,
                      borderBottom: s.dashed ? '2px dashed' : '2px solid',
                      borderColor: color,
                      flexShrink: 0,
                    }}
                  />
                )}
                {!xs && (
                  <Typography
                    variant="caption"
                    sx={{ color: hidden ? tokens.text3 : 'text.secondary' }}
                  >
                    {label}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
